import { baseStore } from "../store"; // Adjust the import based on your file structure
import Logger from "../helpers/logger";
import ErrorTracker from "../helpers/errorTracker";
import { Rental } from "../../types/rental";
import { lockersApiSlice } from "../../api/lockers/lockersApiSlice";

const openLockerCall = async (rental: Rental) => {
  const data = await baseStore.dispatch(
    lockersApiSlice.endpoints.openLocker.initiate({
      depotId: rental.doors[0].depotId,
      door: rental.doors[0].door,
      orderItemId: rental.orderItemId,
      token: rental.token,
    })
  );

  return data;
};
const checkDoorStatus = async (rental: Rental) => {
  const data = await baseStore.dispatch(
    lockersApiSlice.endpoints.getDoorStatus.initiate({
      depotId: rental.doors[0].depotId,
      door: rental.doors[0].door,
    })
  );

  return data;
};

export interface OpenLockerResult {
  /** True only when the backend accepted the UNLOCK. */
  ok: boolean;
  /**
   * HTTP status when the backend refused, undefined for network or client-side
   * failures. Callers need it to tell a deliberate refusal from an
   * infrastructure blip: a 409 means the overtime allowance is spent (#1386)
   * and retrying can never succeed, so "please try again" is the wrong advice.
   */
  status?: number;
}

export const useOpenLocker = (rental: Rental | undefined) => {
  // Reports ok:true only when the locker actually opened. Previously every
  // error was swallowed and the caller advanced as if it had succeeded, so
  // a failed open (server timeout, invalid token, offline station, or
  // missing door data) looked identical to success — the user saw the
  // "locker is open" screen while the door stayed shut. Report the outcome
  // so the caller can surface it and let the user retry.
  const openLocker = async (): Promise<OpenLockerResult> => {
    if (!rental) {
      return { ok: false };
    }
    try {
      const result = await openLockerCall(rental);
      if (result && "error" in result && result.error) {
        throw result.error;
      }
      return { ok: true };
    } catch (error) {
      Logger.for("openLocker").error(error);
      ErrorTracker.trackException(error as Error);
      // axiosBaseQuery normalises API failures to { status, data }; anything
      // else (a thrown Error) simply has no status.
      return { ok: false, status: (error as { status?: number })?.status };
    }
  };

  return openLocker;
};
export const useCheckDoorStatus = (rental: Rental | undefined) => {
  const doorStatus = async () => {
    if (!rental) {
      return { error: new Error("No rental provided") };
    }
    try {
      const result = await checkDoorStatus(rental);
      return result;
    } catch (error) {
      Logger.for("doorStatus").error(error);
      ErrorTracker.trackException(error as Error);
      return { error: error as Error };
    }
  };

  return doorStatus;
};

/**
 * Which failure message an unsuccessful open deserves.
 *
 * Pure, so the decision is testable and identical everywhere. 409 is the
 * backend refusing on purpose — the single opening allowed after the booking
 * ended is already spent (#1386) — and "please try again" is a dead end there,
 * because the retry cannot succeed. Every other failure is worth retrying.
 *
 * Returns a key SUFFIX, not a full i18n key: each flow owns its own namespace
 * (`use_order.<flow>.open_locker.*`), so the decision is shared while the copy
 * stays per flow.
 */
export const openLockerFailureKey = (
  attempt: OpenLockerResult,
): "overtime_used" | "error" =>
  (attempt.status === 409 ? "overtime_used" : "error");

/**
 * Door states the device reports while the locker is SHUT.
 *
 * Mirrors `backend/src/core/lib/shadow.ts` (`CLOSED_DOOR_STATES`), which is
 * authoritative and itself mirrors the kolula-monitoring reconciliation:
 * anything that is not one of these counts as opened — the backend's own
 * comment names `'unlocked'` explicitly.
 *
 * ONE RULE, TWO COPIES — they cannot import each other (separate deployables).
 * An `=== "open"` test here would be a THIRD definition, and a device reporting
 * `'unlocked'` would open physically while the customer is told it did not.
 */
const CLOSED_DOOR_STATES = new Set(["closed", "locked"]);

/** Is this reported state an open door? Empty/absent is NOT an answer. */
export const isDoorStateOpen = (state: unknown): boolean => {
  const value = String(state ?? "").trim().toLowerCase();
  if (value.length === 0) return false;
  return !CLOSED_DOOR_STATES.has(value);
};

/**
 * Confirm the locker PHYSICALLY opened, by polling the reported door status.
 *
 * The backend `openDoor()` is fire-and-forget: it returns success as soon as
 * AWS IoT accepts the desired "UNLOCK" state, without confirming the controller
 * actuated. So an HTTP 2xx is not an open door — if the station controller is
 * offline or slow, the door stays shut while the API reports success. This is
 * the read-back CLAUDE.md requires before the UI may advance ("Hardware
 * Commands: Never Fake Success", #1316/#1319/#1333).
 *
 * Best-effort within the window: transient status-read errors are ignored and
 * retried; a healthy locker reports "open" within ~1-3 s.
 *
 * Extracted from `equipmentRental/UseOrderFlow`, which was the ONLY one of the
 * eight `useOpenLocker` call sites that checked anything at all. It lives here,
 * beside the hook whose result it completes, so the next flow to be migrated
 * imports the answer instead of copying it.
 */
export const useConfirmDoorOpened = (rental: Rental | undefined) => {
  const checkDoorStatus = useCheckDoorStatus(rental);

  return async (): Promise<boolean> => {
    const MAX_ATTEMPTS = 10; // ~10s budget
    const POLL_INTERVAL_MS = 1000;
    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
      baseStore.dispatch(lockersApiSlice.util.invalidateTags(["doorStatus"]));
      try {
        const result = (await checkDoorStatus()) as {
          data?: { doorStatus?: unknown };
        };
        if (isDoorStateOpen(result?.data?.doorStatus)) {
          return true;
        }
      } catch {
        // ignore transient read errors; keep polling within the window
      }
      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
    }
    return false;
  };
};
